<ng-template
  let-extraClass="extraClass"
  #topNav>
  @if (!isRightSideContent) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="'Send Payment'"
      [canGoBack]="true" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>

  <tidy-wrapper>

    @if (loaded) {
      @if (userRole !== 'member' && creditBalance > 0) {
        <tidy-row [align]="'center'">
          <tidy-text
            [body]="'You have'" /><span>&nbsp;</span>
            <tidy-text
              [body]="creditBalance | tcurrency" /><span>&nbsp;</span>
              <tidy-text
                [body]="'credit. Your credit will be used first.'" />
              <span>&nbsp;</span>
              <tidy-text
                [body]="'If you want to send more, we will charge your card for the difference.'" />
            </tidy-row>
          }
          @if (!hasCreditCard && creditBalance == 0) {
            <tidy-row [align]="'center'">
              <tidy-text
                [body]="'Please add a credit/debit card to your account to send payment.'" />
              @if (creditBalance > 0) {
                <span>&nbsp;</span>
                <tidy-text
                  [body]="'If you want to send more, we will charge your card for the difference.'" />
              }
            </tidy-row>
            <tidy-button
              [text]="'Add Credit/Debit Card'"
              [action]="boundGoToAddCard"
              class="secondary" />
          }
          <tidy-card>
            <form [formGroup]="form">
              <tidy-row class="field-bottom-padding">
                <tidy-text
                  [header]="'How much do you want to send?'" />
              </tidy-row>
              <tidy-row>
                <tidy-input
                  formControlName="price"
                  [label]="'Amount'"
                  [form]="form.controls.price"
                  [icon]="'assets/svg/card.svg'"
                  [errorMessage]="'Please select an amount.'"
                  [submitted]="submitted"
                  inputMode="numeric"
                  [mask]="'currencyMask'" />
              </tidy-row>
            </form>
          </tidy-card>
          <tidy-card>
            <form [formGroup]="form">
              <tidy-row class="extra-bottom-padding">
                <tidy-text
                  [header]="'What is it for?'" />
              </tidy-row>
              <tidy-row>
                <tidy-datepicker
                  label="Job Date"
                  [icon]="'assets/svg/calendar.svg'"
                  formControlName="date"
                  [form]="form.controls.date"
                  errorMessage="Please enter a valid date."
                  [submitted]="submitted" />
              </tidy-row>
              <tidy-row>
                <tidy-select
                  [label]="'Job Time'"
                  [icon]="'assets/svg/time.svg'"
                  [form]="form.controls.time"
                  formControlName="time"
                  [items]="jobTimeItems" />
              </tidy-row>
              <tidy-row>
                <tidy-select
                  [label]="'Service'"
                  [icon]="'assets/svg/briefcase.svg'"
                  [items]="serviceCategoryItems"
                  (optionChange)="parseServiceItemsByCategory($event)"
                  [form]="form.controls.serviceCategory"
                  formControlName="serviceCategory" />
              </tidy-row>
              @if (serviceItems?.length) {
                <tidy-row>
                  <tidy-radio-button
                    formControlName="service"
                    [form]="form.controls.service"
                    [items]="serviceItems" />
                </tidy-row>
              }
              @if (!serviceItems?.length) {
                <tidy-row class="extra-top-padding">
                  <tidy-text
                    [helper]="'No services offered.'" />
                </tidy-row>
              }
            </form>
          </tidy-card>

          @if (hasCreditCard && howToPayItems?.length > 1) {
            <tidy-card>
              <form [formGroup]="form">
                <tidy-row class="field-bottom-padding">
                  <tidy-text
                    [header]="'Payment Method'" />
                </tidy-row>
                <tidy-row>
                  <tidy-radio-button
                    formControlName="howToPay"
                    [form]="form.controls.howToPay"
                    [items]="howToPayItems" />
                </tidy-row>
              </form>
            </tidy-card>
          }

          <tidy-card>
            <tidy-row class="extra-bottom-padding">
              <tidy-text
                [title]="'Payment Summary'" />
            </tidy-row>
            @if (form.value.price === '') {
              <tidy-row>
                <tidy-text
                  [body]="'Enter a price to see the payment summary.'" />
              </tidy-row>
            }
            @if (!paymentPreview && form.value.price !== '') {
              <tidy-loader />
            }
            @if (form.value.howToPay !== 'newCard' && form.value.howToPay !== 'alreadyPaid' && paymentPreview) {
              @for (item of paymentPreview.items; track item) {
                @for (payment of item.preview.cleaning_detail.payments; track payment) {
                  @if (payment.amount > 0) {
                  <tidy-row>
                    <tidy-text
                      [body]="payment.from_type_name || 'Credit Card'"/>
                    <tidy-text
                      [body]="' - ' + (payment.amount | tcurrency)"/>
                    @if (payment.fee.amount > 0) {
                      <tidy-text
                        [body]="' + ' + (payment.fee.amount | tcurrency) + ' (' + payment.fee.percentage + '% ' + ('processing fee' | translate) + ')'"/>
                    }
                  </tidy-row>
                  }
                }
              }
              <tidy-row class="extra-top-padding">
                <tidy-text
                  [header]="'Total Cost: '" />
                <tidy-text
                  [header]="totalOwed | tcurrency" />
              </tidy-row>
            }
          </tidy-card>

          <tidy-button
            [text]="'Send Payment'"
            [action]="boundSendPayment"
            class="primary" />
          @if (errorMessage) {
            @if (!errorMessage.includes('Customer Token not found') && !errorMessage.includes('Credit Card is required to send a payment!') && !errorMessage?.includes('Your card was declined')) {
              <tidy-error-message
                [text]="errorMessage" />
            }
            @if (errorMessage.includes('Customer Token not found') || errorMessage.includes('Credit Card is required to send a payment!') || errorMessage?.includes('Your card was declined')) {
              @if (!errorMessage?.includes('Your card was declined')) {
                <tidy-error-message
                  [text]="'Please add a credit/debit card to send this amount. Any credit in your account will be used before any charges.'" />
              }
              @if (errorMessage?.includes('Your card was declined')) {
                <tidy-error-message
                  [text]="'Your card was declined. Please add a new credit/debit card. Any credit in your account will be used before any charges.'" />
              }
              <tidy-button
                [text]="'Add Credit/Debit Card'"
                [action]="boundGoToAddCard"
                class="secondary" />
            }
          }
        }

        @if (!loaded) {
          <tidy-card>
            <tidy-row class="extra-bottom-padding">
              <ion-skeleton-text animated style="width: 90%; height: 18px" />
            </tidy-row>
            <tidy-row class="extra-bottom-padding">
              <ion-skeleton-text animated style="width: 95%; height: 55px" />
            </tidy-row>
          </tidy-card>
          <tidy-card>
            <tidy-row class="extra-bottom-padding">
              <ion-skeleton-text animated style="width: 38%; height: 18px" />
            </tidy-row>
            <tidy-row class="extra-bottom-padding">
              <ion-skeleton-text animated style="width: 95%; height: 55px" />
            </tidy-row>
            <tidy-row class="extra-bottom-padding">
              <ion-skeleton-text animated style="width: 95%; height: 55px" />
            </tidy-row>
            <tidy-row class="extra-bottom-padding">
              <ion-skeleton-text animated style="width: 95%; height: 55px" />
            </tidy-row>
            <tidy-row class="extra-bottom-padding">
              <ion-skeleton-text animated style="width: 45%; height: 18px" />
            </tidy-row>
          </tidy-card>
        }
      </tidy-wrapper>
    </ng-template>

    @if (!isRightSideContent) {
      <app-scroll-wrapper [templateContent]="content" />
    }

    @if (isRightSideContent) {
      <ion-content class="ion-content-right-column">
        <ng-container *ngTemplateOutlet="content" />
      </ion-content>
    }
